import type {
  FunnelBucket,
  FunnelMetric,
  FunnelOutcomes,
  FunnelSummaryResponse,
  FunnelStages,
} from "../../types/dealFunnel";
import {
  closedApolloMetric,
  nonApolloClosedMetric,
} from "../../pages/pipeline/summary/funnelClosedMetrics";
import {
  COLORS,
  FONTS,
  SLIDE_W,
  TABLE_ROW_BORDER,
  TITLE_H,
  addShape,
  addTableHeaderRule,
  addTitleBar,
  getShapes,
  type PptxInstance,
  type PptxSlide,
} from "./pptxCore";

type DealFlowTableRow = {
  label: string;
  isTotal: boolean;
  stages: FunnelStages;
  outcomes: FunnelOutcomes;
};

const COLUMN_WIDTHS = [
  0.95, 1.25, 1.4, 0.8, 0.75, 1.0, 1.2, 1.3, 1.2, 1.4, 1.4,
];

const HEADERS = [
  "Bucket",
  "Received Materials",
  "Term Sheet Submitted",
  "Signed",
  "Closed",
  "Closed - Apollo",
  "Lost To Other Lender",
  "Killed After Term Sheet",
  "Killed After Signing",
  "Term Sheet Submitted %",
  "Deals Won %",
];

function emptyMetric(): FunnelMetric {
  return { count: 0, volume_gbp: 0 };
}

function normalizeOutcomes(outcomes: FunnelOutcomes): FunnelOutcomes {
  return {
    lost_to_other_lender: outcomes.lost_to_other_lender ?? emptyMetric(),
    killed_after_term_sheet: outcomes.killed_after_term_sheet ?? emptyMetric(),
    killed_after_signing: outcomes.killed_after_signing ?? emptyMetric(),
  };
}

function buildRows(summary: FunnelSummaryResponse): DealFlowTableRow[] {
  const buckets = [...summary.buckets].sort((a, b) => {
    if (a.id == null) return 1;
    if (b.id == null) return -1;
    return a.id - b.id;
  });

  return [
    {
      label: "Total",
      isTotal: true,
      stages: summary.totals.stages,
      outcomes: normalizeOutcomes(summary.totals.outcomes),
    },
    ...buckets.map((bucket: FunnelBucket) => ({
      label: bucket.name,
      isTotal: false,
      stages: bucket.stages,
      outcomes: normalizeOutcomes(bucket.outcomes),
    })),
  ];
}

function formatVolume(metric: FunnelMetric): string {
  if (!Number.isFinite(metric.volume_gbp) || metric.volume_gbp === 0) {
    return "-";
  }
  return `£${(metric.volume_gbp / 1_000_000).toLocaleString("en-GB", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}`;
}

function formatRate(rate: number | undefined): string {
  if (rate == null || Number.isNaN(rate)) return "—";
  return `${(rate * 100).toLocaleString("en-GB", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

function tableCell(
  text: string,
  options: {
    bold?: boolean;
    align?: "left" | "right";
    fill?: string;
  } = {},
) {
  return {
    text,
    options: {
      fontFace: FONTS.body,
      fontSize: 8,
      color: COLORS.textDark,
      bold: options.bold ?? false,
      align: options.align ?? "right",
      valign: "middle" as const,
      fill: options.fill ? { color: options.fill } : undefined,
      margin: [0.03, 0.07, 0.03, 0.07] as [number, number, number, number],
    },
  };
}

function buildTableRows(rows: DealFlowTableRow[]) {
  const headerFill = COLORS.white;
  const header = HEADERS.map((label, index) =>
    tableCell(label, {
      bold: true,
      align: index === 0 ? "left" : "right",
      fill: headerFill,
    }),
  );

  const body = rows.map((row) => {
    const fill = row.isTotal ? COLORS.totalRow : COLORS.white;
    const s = row.stages;
    const o = row.outcomes;
    return [
      tableCell(row.label, { bold: row.isTotal, align: "left", fill }),
      tableCell(formatVolume(s.received_materials), {
        bold: row.isTotal,
        fill,
      }),
      tableCell(formatVolume(s.term_sheet_submitted), {
        bold: row.isTotal,
        fill,
      }),
      tableCell(formatVolume(s.reached_signed), { bold: row.isTotal, fill }),
      tableCell(formatVolume(nonApolloClosedMetric(s)), {
        bold: row.isTotal,
        fill,
      }),
      tableCell(formatVolume(closedApolloMetric(s)), {
        bold: row.isTotal,
        fill,
      }),
      tableCell(formatVolume(o.lost_to_other_lender), {
        bold: row.isTotal,
        fill,
      }),
      tableCell(formatVolume(o.killed_after_term_sheet), {
        bold: row.isTotal,
        fill,
      }),
      tableCell(formatVolume(o.killed_after_signing), {
        bold: row.isTotal,
        fill,
      }),
      tableCell(formatRate(s.term_sheet_submitted.rate_volume_from_received), {
        bold: row.isTotal,
        fill,
      }),
      tableCell(formatRate(s.reached_signed.rate_volume_from_tss), {
        bold: row.isTotal,
        fill,
      }),
    ];
  });

  return [header, ...body];
}

function addRateDataBars(
  pptx: PptxInstance,
  slide: PptxSlide,
  rows: DealFlowTableRow[],
  tableX: number,
  bodyTop: number,
  rowH: number,
): void {
  const { ROUNDED_RECTANGLE } = getShapes(pptx);
  const firstRateX = tableX + COLUMN_WIDTHS.slice(0, 9).reduce((a, b) => a + b, 0);
  const rateValues = rows.map((row) => [
    row.stages.term_sheet_submitted.rate_volume_from_received,
    row.stages.reached_signed.rate_volume_from_tss,
  ]);
  const trackH = 0.09;

  rateValues.forEach((rates, rowIndex) => {
    rates.forEach((rate, rateIndex) => {
      if (rate == null || Number.isNaN(rate)) return;
      const cellX =
        firstRateX + (rateIndex === 0 ? 0 : COLUMN_WIDTHS[9]);
      const trackX = cellX + 0.08;
      const trackW = COLUMN_WIDTHS[9 + rateIndex] * 0.48;
      const y = bodyTop + rowH * rowIndex + (rowH - trackH) / 2;

      addShape(slide, ROUNDED_RECTANGLE, {
        x: trackX,
        y,
        w: trackW,
        h: trackH,
        fill: { color: "F1F3F5" },
        line: { color: "F1F3F5", width: 0 },
        rectRadius: trackH / 2,
      });

      const fraction = Math.min(Math.max(rate, 0), 1);
      if (fraction === 0) return;
      addShape(slide, ROUNDED_RECTANGLE, {
        x: trackX,
        y,
        w: Math.max(trackW * fraction, trackH),
        h: trackH,
        fill: { color: "1B7A63" },
        line: { color: "1B7A63", width: 0 },
        rectRadius: trackH / 2,
      });
    });
  });
}

export function addDealFlowQuarterlySlide(
  pptx: PptxInstance,
  summary: FunnelSummaryResponse,
  logoDataUri?: string | null,
): PptxSlide {
  const slide = pptx.addSlide();
  slide.background = { color: COLORS.white };

  addTitleBar(pptx, slide, {
    title: "Pipeline Analysis – Deal Flow Summary (cont.)",
    logoDataUri,
  });

  slide.addText("By Quarter  •  Volume (£GBP MM)", {
    x: 0.28,
    y: TITLE_H + 0.18,
    w: SLIDE_W - 0.56,
    h: 0.26,
    fontFace: FONTS.body,
    fontSize: 9,
    color: COLORS.muted,
    align: "right",
    margin: 0,
  });

  const rows = buildRows(summary);
  const tableX = 0.28;
  const tableY = TITLE_H + 0.58;
  const tableW = COLUMN_WIDTHS.reduce((a, b) => a + b, 0);
  const maxBodyH = 5.5;
  // Roomier header so a wrapped column label cannot shift the rule or the bars.
  const headerRowH = 0.42;
  const bodyRowH = Math.min(0.5, Math.max(0.3, maxBodyH / rows.length));
  const tableH = headerRowH + bodyRowH * rows.length;

  slide.addTable(buildTableRows(rows), {
    x: tableX,
    y: tableY,
    w: tableW,
    h: tableH,
    colW: COLUMN_WIDTHS,
    rowH: [headerRowH, ...rows.map(() => bodyRowH)],
    border: TABLE_ROW_BORDER,
    fontFace: FONTS.body,
    fontSize: 8,
    color: COLORS.textDark,
    valign: "middle",
  });

  addTableHeaderRule(pptx, slide, {
    x: tableX,
    y: tableY,
    w: tableW,
    headerRowH,
  });

  addRateDataBars(pptx, slide, rows, tableX, tableY + headerRowH, bodyRowH);

  slide.addText(
    "Note: Quarters represent the date the deal was received. All subsequent outcomes are reported against the original received quarter to measure performance.",
    {
      x: tableX,
      y: tableY + tableH + 0.12,
      w: tableW,
      h: 0.32,
      fontFace: FONTS.body,
      fontSize: 8,
      color: COLORS.muted,
      italic: true,
      align: "left",
      valign: "top",
      margin: 0,
    },
  );

  return slide;
}
